import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { CheckCircle, Circle, ArrowRight, X, User, MapPin, FileText, Camera, Hash, Film, Euro, Instagram, ChevronDown, ChevronUp, Building2, Globe, Users, Briefcase, BookOpen } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'

const ProfileCompletionBanner = ({ onNavigateToProfile }) => {
  const { user, needsProfileCompletion } = useAuth()
  const [dismissed, setDismissed] = useState(false)
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    if (dismissed) {
      const timer = setTimeout(() => {
        setDismissed(false)
      }, 60000)
      return () => clearTimeout(timer)
    }
  }, [dismissed])

  if (!needsProfileCompletion || dismissed) return null

  const isCompany = user?.role === 'bedrijf'
  const isAgency = user?.role === 'agency'

  // Build checklist items with completion status
  const getChecklistItems = () => {
    if (isCompany) {
      return [
        { key: 'companyName', label: 'Bedrijfsnaam', hint: 'Voeg je bedrijfsnaam toe', done: !!user?.companyName, icon: Building2 },
        { key: 'contactPerson', label: 'Contactpersoon', hint: 'Wie is het aanspreekpunt?', done: !!user?.contactPerson, icon: User },
        { key: 'companyBio', label: 'Bedrijfsomschrijving', hint: 'Beschrijf je bedrijf', done: !!user?.companyBio, icon: FileText },
        { key: 'website', label: 'Website', hint: 'Voeg je website URL toe', done: !!user?.website, icon: Globe },
        { key: 'industry', label: 'Branche', hint: 'In welke branche ben je actief?', done: !!user?.industry, icon: Briefcase },
        { key: 'companySize', label: 'Bedrijfsgrootte', hint: 'Hoeveel medewerkers?', done: !!user?.companySize, icon: Users },
        { key: 'location', label: 'Locatie', hint: 'In welke stad zit je?', done: !!user?.location?.city, icon: MapPin },
      ]
    }

    if (isAgency) {
      return [
        { key: 'agencyName', label: 'Bureau naam', hint: 'Voeg je bureaunaam toe', done: !!user?.agencyName, icon: Building2 },
        { key: 'contactPerson', label: 'Contactpersoon', hint: 'Wie is het aanspreekpunt?', done: !!user?.contactPerson, icon: User },
        { key: 'agencyBio', label: 'Bureau omschrijving', hint: 'Beschrijf je bureau', done: !!user?.agencyBio, icon: FileText },
        { key: 'agencyLogo', label: 'Logo', hint: 'Upload je bureau logo', done: !!user?.agencyLogo, icon: Camera },
        { key: 'location', label: 'Locatie', hint: 'In welke stad zit je?', done: !!user?.location?.city, icon: MapPin },
        { key: 'agencySize', label: 'Bureau grootte', hint: 'Hoeveel medewerkers?', done: !!user?.agencySize, icon: Users },
        { key: 'specializations', label: 'Specialisaties', hint: 'Voeg specialisaties toe', done: user?.specializations?.length > 0, icon: Hash },
      ]
    }

    // Creator
    return [
      { key: 'firstName', label: 'Voornaam', hint: 'Vul je voornaam in', done: !!user?.firstName, icon: User },
      { key: 'lastName', label: 'Achternaam', hint: 'Vul je achternaam in', done: !!user?.lastName, icon: User },
      { key: 'bio', label: 'Bio', hint: 'Schrijf een korte beschrijving over jezelf', done: !!user?.bio, icon: FileText },
      { key: 'profilePicture', label: 'Profielfoto', hint: 'Upload een profielfoto', done: !!user?.profilePictureUrl, icon: Camera },
      { key: 'location', label: 'Locatie', hint: 'In welke stad woon je?', done: !!user?.location?.city, icon: MapPin },
      { key: 'age', label: 'Leeftijd', hint: 'Vul je geboortedatum in', done: !!user?.age, icon: User },
      { key: 'niches', label: 'Niches / Specialisaties', hint: 'Selecteer je content niches', done: user?.niches?.length > 0, icon: Hash },
      { key: 'portfolio', label: "Portfolio video's", hint: 'Upload voorbeelden van je werk', done: user?.portfolio?.length > 0, icon: Film },
      { key: 'pricing', label: 'Tarief', hint: 'Stel je basis tarief in', done: !!user?.pricing?.baseRate, icon: Euro },
      { key: 'socialMedia', label: 'Social media', hint: 'Koppel Instagram of TikTok', done: !!(user?.socialMedia?.instagram?.username || user?.socialMedia?.tiktok?.username), icon: Instagram },
    ]
  }

  const items = getChecklistItems()
  const completedCount = items.filter(i => i.done).length
  const totalCount = items.length
  const percentage = Math.round((completedCount / totalCount) * 100)
  const missingItems = items.filter(i => !i.done)

  // Show max 3 items when collapsed
  const visibleMissing = expanded ? missingItems : missingItems.slice(0, 3)
  const hasMore = missingItems.length > 3

  return (
    <div className="relative overflow-hidden rounded-2xl border border-amber-200 bg-white shadow-md">
      {/* Header */}
      <div className="px-4 py-3 md:px-6 md:py-4 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50">
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-amber-400 flex items-center justify-center">
                <span className="text-white font-bold text-sm">{percentage}%</span>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-[#0F172A]">
                  Profiel {percentage}% compleet
                </p>
                <p className="text-xs text-[#475569]">
                  {missingItems.length === 0 ? 'Alles ingevuld!' : `Nog ${missingItems.length} ${missingItems.length === 1 ? 'veld' : 'velden'} aanvullen`}
                </p>
              </div>
            </div>

            {/* Progress bar */}
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700 ease-out"
                style={{
                  width: `${percentage}%`,
                  backgroundColor: percentage < 40 ? '#f59e0b' : percentage < 70 ? '#3b82f6' : '#10b981'
                }}
              />
            </div>
          </div>

          {/* Onder sm alleen de sluitknop: "Aanvullen" nam ~110px van de 150px die overbleef voor tekst en balk;
              de checklist-items hieronder zijn zelf al tikbaar en gaan naar dezelfde plek. */}
          <div className="flex items-center gap-2 flex-shrink-0 self-start sm:self-auto">
            {onNavigateToProfile ? (
              <button
                onClick={onNavigateToProfile}
                className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 bg-[#0F172A] text-white text-sm font-semibold rounded-lg hover:bg-[#1e293b] transition whitespace-nowrap"
              >
                Aanvullen
                <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <Link
                to="/profile"
                className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 bg-[#0F172A] text-white text-sm font-semibold rounded-lg hover:bg-[#1e293b] transition whitespace-nowrap"
              >
                Aanvullen
                <ArrowRight className="w-4 h-4" />
              </Link>
            )}
            <button
              onClick={() => setDismissed(true)}
              className="p-2.5 -mr-1 text-[#475569] hover:text-[#0F172A] hover:bg-amber-100 rounded-lg transition"
              title="Tijdelijk verbergen"
              aria-label="Tijdelijk verbergen"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Checklist */}
      <div className="px-4 py-3 md:px-6 md:py-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {visibleMissing.map((item) => {
            const Icon = item.icon
            const ItemComponent = onNavigateToProfile ? 'button' : Link
            const itemProps = onNavigateToProfile ? { onClick: onNavigateToProfile } : { to: '/profile' }
            return (
              <ItemComponent
                key={item.key}
                {...itemProps}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-amber-100 bg-amber-50/50 hover:bg-amber-50 hover:border-amber-200 transition group text-left w-full"
              >
                <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center group-hover:bg-amber-200 transition">
                  <Icon className="w-4 h-4 text-amber-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#0F172A] truncate">{item.label}</p>
                  <p className="text-xs text-[#475569] truncate">{item.hint}</p>
                </div>
                <Circle className="w-4 h-4 text-amber-300 flex-shrink-0 ml-auto" />
              </ItemComponent>
            )
          })}

          {/* Show completed items summary */}
          {completedCount > 0 && expanded && items.filter(i => i.done).map((item) => {
            const Icon = item.icon
            return (
              <div
                key={item.key}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-emerald-100 bg-emerald-50/50"
              >
                <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <Icon className="w-4 h-4 text-emerald-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#0F172A] truncate">{item.label}</p>
                  <p className="text-xs text-emerald-600 truncate">Ingevuld</p>
                </div>
                <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 ml-auto" />
              </div>
            )
          })}
        </div>

        {/* Expand/collapse button */}
        {(hasMore || expanded) && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="mt-3 flex items-center gap-1.5 text-sm font-medium text-[#475569] hover:text-[#1D8BF8] transition mx-auto"
          >
            {expanded ? (
              <>
                Minder tonen
                <ChevronUp className="w-4 h-4" />
              </>
            ) : (
              <>
                Alle {missingItems.length} ontbrekende velden tonen
                <ChevronDown className="w-4 h-4" />
              </>
            )}
          </button>
        )}
      </div>
    </div>
  )
}

export default ProfileCompletionBanner
